import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

function Viewer({ items, start, onClose }) {
  const [i, setI] = useState(start);
  const n = items.length;
  const touchX = useRef(null);
  const prev = useCallback(() => setI((x) => (x - 1 + n) % n), [n]);
  const next = useCallback(() => setI((x) => (x + 1) % n), [n]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    const o = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = o; };
  }, [prev, next, onClose]);
  useEffect(() => { // preload neighbors
    [items[(i + 1) % n], items[(i - 1 + n) % n]].forEach((g) => { const im = new Image(); im.src = g.src; });
  }, [i, n, items]);
  const g = items[i];
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col select-none" onClick={onClose} data-testid="gallery-viewer"
      onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchX.current == null) return;
        const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null;
        if (Math.abs(dx) > 50) (dx > 0 ? prev() : next());
      }}>
      <div className="flex items-center justify-between px-3 py-2 text-cream" onClick={stop}>
        <span className="font-heading font-bold text-sm" data-testid="gallery-viewer-count">{i + 1} / {n}</span>
        <button type="button" onClick={onClose} aria-label="Close photos" className="w-11 h-11 inline-flex items-center justify-center rounded-full hover:bg-white/10"><X className="w-7 h-7" /></button>
      </div>
      <div className="relative flex-1 flex items-center justify-center px-2 min-h-0">
        <img src={g.src} alt={g.alt} title={g.title} width={g.w} height={g.h} onClick={stop} className="max-w-full max-h-full object-contain rounded-lg" />
        <button type="button" onClick={(e) => { stop(e); prev(); }} aria-label="Previous photo" data-testid="gallery-viewer-prev"
          className="absolute left-2 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-black/50 text-cream hover:bg-black/70"><ChevronLeft className="w-7 h-7" /></button>
        <button type="button" onClick={(e) => { stop(e); next(); }} aria-label="Next photo" data-testid="gallery-viewer-next"
          className="absolute right-2 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center rounded-full bg-black/50 text-cream hover:bg-black/70"><ChevronRight className="w-7 h-7" /></button>
      </div>
      <p className="text-center text-cream font-body px-4 py-4" onClick={stop}>{g.caption}</p>
    </div>,
    document.body
  );
}

export default function GalleryGrid({ items, className = "" }) {
  const [open, setOpen] = useState(null);
  return (
    <>
      <div className={`grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3 ${className}`} data-testid="gallery-grid">
        {items.map((g, idx) => (
          <button key={g.src} type="button" onClick={() => setOpen(idx)} data-testid="gallery-thumb"
            className="group relative block overflow-hidden rounded-xl border-2 border-charcoal shadow-brutsm bg-charcoal text-left aspect-square">
            <img src={g.sm} alt={g.alt} title={g.title} loading="lazy" decoding="async" width={g.sw} height={g.sh}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          </button>
        ))}
      </div>
      {open !== null && <Viewer items={items} start={open} onClose={() => setOpen(null)} />}
    </>
  );
}
